import { message } from "antd";
import {
  AlertCircle,
  ArrowRight,
  Eye,
  EyeOff,
  KeyRound,
  Layers,
  Loader2,
  Lock,
  User,
  X,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { scDarkFullLogo } from "../../../assets/images/index";
import { config } from "../../../config";
import { Regex } from "../../../config/constants";
import { ModalTitle } from "../../../config/enum";
import {
  setAuthInfo,
  setLogin,
} from "../../../redux/features/auth/authSlice";
import { storage } from "../../../utils/storage";
import {
  useForgotPasswordSendMailMutation,
  useReportsLoginMutation,
} from "../../../redux/services/authApiSlice";
import { Page } from "../../../routes/config";
import "./style.css";

// Environment badge — reflects the build's real config.ENV. No entry for
// "prod": a live production login shouldn't call out which backend it uses.
const ENV_BADGES: Record<string, { label: string; color: string }> = {
  local: { label: "LOCAL ENVIRONMENT", color: "orange" },
  dev: { label: "DEV ENVIRONMENT", color: "sky" },
  qa: { label: "QA ENVIRONMENT", color: "sky" },
  load: { label: "LOAD ENVIRONMENT", color: "sky" },
};

const getMutationErrorMessage = (error: unknown): string => {
  const typed = error as { data?: { error?: { message?: string } } };
  return typed?.data?.error?.message ?? "Something went wrong";
};

export const Login = () => {
  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [forgotPassword, setForgotPassword] = useState<{ email: string }>({
    email: "",
  });
  const [errorAlert, setErrorAlert] = useState(false);
  const [loginError, setLoginError] = useState(false);
  const [emailError, setEmailError] = useState<string | undefined>(undefined);
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState<{
    username?: string;
    password?: string;
  }>({});

  const activeEnvBadge = ENV_BADGES[config.ENV];

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [userLogin, userLoginData] = useReportsLoginMutation();
  const [forgotPasswordReq, forgotPasswordInfo] =
    useForgotPasswordSendMailMutation();

  useEffect(() => {
    if (userLoginData?.isSuccess) {
      const loginResponse = userLoginData?.data as {
        result?: {
          accessToken?: string;
          idToken?: string;
          refreshToken?: string;
          tenantInfo?: unknown;
          userInfo?: { userRole?: { userId?: string; isPlatformRole?: boolean } };
        };
      };
      const { accessToken, idToken, refreshToken, tenantInfo, userInfo } =
        loginResponse?.result ?? {};

      dispatch(
        setAuthInfo({
          userInfo,
          accessToken,
          idToken,
          refreshToken,
          userId: userInfo?.userRole?.userId,
          tenantInfo,
          isPlatformRole: userInfo?.userRole?.isPlatformRole,
        }),
      );
      dispatch(setLogin(true));
      // Persist auth context so guarded routes survive refresh/navigation.
      if (accessToken) storage.setAccessToken(accessToken);
      if (idToken) storage.setIdToken(idToken);
      if (refreshToken) storage.setRefreshToken(refreshToken);
      if (tenantInfo) {
        const ti = tenantInfo as { id?: string; tenantId?: string };
        storage.setTenantInfo({ ...ti, id: ti.id ?? ti.tenantId });
      }
      if (userInfo) storage.setUser(userInfo);
      navigate(Page.REPORTS_ENABLEMENT);
    }
  }, [dispatch, navigate, userLoginData?.data, userLoginData?.isSuccess]);

  useEffect(() => {
    if (userLoginData?.isError) {
      setLoginError(true);
    }
  }, [userLoginData?.error, userLoginData?.isError]);

  useEffect(() => {
    if (forgotPasswordInfo?.isSuccess) {
      message.success("Email sent Successfully");
      setIsModalVisible(false);
      setForgotPassword({ email: "" });
      setEmailError(undefined);
      setErrorAlert(false);
    }
  }, [forgotPasswordInfo?.isSuccess]);

  useEffect(() => {
    if (forgotPasswordInfo?.isError) {
      setErrorAlert(true);
    }
  }, [forgotPasswordInfo?.isError]);

  const validateForm = () => {
    const errors: { username?: string; password?: string } = {};
    if (!credentials.username) {
      errors.username = "Please enter your username";
    } else if (!/^\S{6,}$/.test(credentials.username)) {
      errors.username = "Please enter a valid username";
    }

    if (!credentials.password) {
      errors.password = "Please enter your password";
    } else if (!new RegExp(Regex.PASSWORD_VALID_LENGTH).test(credentials.password)) {
      errors.password = "Please enter a valid password";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onFinish = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      userLogin(credentials);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setErrorAlert(false);
    setLoginError(false);
    setFormErrors((prev) => ({ ...prev, [e.target.name]: undefined }));
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value,
    });
  };

  const onModalClose = () => {
    setIsModalVisible(false);
    setForgotPassword({ email: "" });
    setEmailError(undefined);
    setErrorAlert(false);
  };

  const onModalSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const email = forgotPassword.email.trim();
    if (!email) {
      setEmailError("Please enter your email address");
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }
    forgotPasswordReq(forgotPassword);
  };

  const onEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setErrorAlert(false);
    setEmailError(undefined);
    setForgotPassword({ email: e.target.value });
  };

  const isAuthenticating = userLoginData.isLoading;

  return (
    <div className="sc-auth-body">
      {/* Background decorators */}
      <div className="sc-bg-grid" />
      <div className="sc-glow-orb-top" />
      <div className="sc-glow-orb-bottom" />

      <div className="sc-layout-container">
        {/* LEFT SHOWCASE PANEL */}
        <div className="sc-showcase-panel">
          <div className="sc-showcase-header">
            <img className="sc-showcase-logo" src={scDarkFullLogo} alt="Smart Cosmos" />
          </div>

          <div className="sc-showcase-content">
            <div className="sc-hero-badge">
              <Layers size={14} strokeWidth={2} />
              <span>
                Precision RFID & NFC Serialization for Physical-to-Digital
                Enterprise.
              </span>
            </div>

            <h1 className="sc-hero-title">
              Connect physical assets to the{" "}
              <span className="sc-highlight-orange">digital world.</span>
            </h1>

            <p className="sc-hero-subtitle">
              Empower your global supply chain with real-time asset tracking,
              digital product passports, and cloud-native authentication.
            </p>

            <div className="sc-metrics-grid">
              <div className="sc-metric-card">
                <div className="sc-metric-val">24.8M+</div>
                <div className="sc-metric-lbl">Digital Passports</div>
              </div>
              <div className="sc-metric-card">
                <div className="sc-metric-val sc-orange">99.99%</div>
                <div className="sc-metric-lbl">Platform Uptime</div>
              </div>
              <div className="sc-metric-card">
                <div className="sc-metric-val">&lt;10ms</div>
                <div className="sc-metric-lbl">Read Latency</div>
              </div>
            </div>
          </div>

          <div className="sc-showcase-footer">
            <div className="sc-specs-row">
              <span className="sc-spec-item">
                <Lock className="sc-emerald" size={16} strokeWidth={2} />
                End-to-End Encryption
              </span>
            </div>
            <span>&copy; 2026 Smart Cosmos. All rights reserved.</span>
          </div>
        </div>

        {/* RIGHT FORM PANEL */}
        <div className="sc-form-panel">
          <div className="sc-form-container">
            {/* The showcase panel (and its logo) is hidden on narrow screens. */}
            <img className="sc-form-logo" src={scDarkFullLogo} alt="Smart Cosmos" />

            {activeEnvBadge && (
              <div className="sc-top-bar">
                <span className={`sc-env-btn sc-env-${activeEnvBadge.color}`}>
                  <span className="sc-env-pulse" />
                  {activeEnvBadge.label}
                </span>
              </div>
            )}

            <div className="sc-form-header">
              <h2 className="sc-form-title">Sign in to Portal</h2>
              <p className="sc-form-subtitle">
                Enter your credentials to access your Smart Cosmos digital asset
                management portal.
              </p>
            </div>

            <form className="sc-form" onSubmit={onFinish} noValidate>
              {loginError && (
                <div className="sc-alert sc-alert-error" role="alert">
                  <AlertCircle size={16} strokeWidth={2} aria-hidden="true" />
                  <span>{getMutationErrorMessage(userLoginData?.error)}</span>
                </div>
              )}

              {/* Username */}
              <div className="sc-field">
                <label className="sc-label" htmlFor="username">
                  USERNAME
                </label>
                <div
                  className={`sc-input-box ${formErrors.username ? "sc-invalid" : ""}`}
                >
                  <div className="sc-input-icon">
                    <User size={16} strokeWidth={2} aria-hidden="true" />
                  </div>
                  <input
                    id="username"
                    type="text"
                    name="username"
                    placeholder="Enter Username"
                    className="sc-input"
                    value={credentials.username}
                    onChange={handleChange}
                    autoComplete="username"
                    required
                  />
                </div>
                {formErrors.username && (
                  <span className="sc-field-error">{formErrors.username}</span>
                )}
              </div>

              {/* Password */}
              <div className="sc-field">
                <div className="sc-label-row">
                  <label className="sc-label" htmlFor="password">
                    PASSWORD
                  </label>
                  <button
                    type="button"
                    className="sc-forgot-link"
                    onClick={() => setIsModalVisible(true)}
                  >
                    Forgot password?
                  </button>
                </div>
                <div
                  className={`sc-input-box ${formErrors.password ? "sc-invalid" : ""}`}
                >
                  <div className="sc-input-icon">
                    <Lock size={16} strokeWidth={2} aria-hidden="true" />
                  </div>
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Enter Password"
                    className="sc-input sc-input-pw"
                    value={credentials.password}
                    onChange={handleChange}
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    className="sc-pw-toggle"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff size={16} strokeWidth={2} aria-hidden="true" />
                    ) : (
                      <Eye size={16} strokeWidth={2} aria-hidden="true" />
                    )}
                  </button>
                </div>
                {formErrors.password && (
                  <span className="sc-field-error">{formErrors.password}</span>
                )}
              </div>

              <button
                type="submit"
                className="sc-btn-submit"
                disabled={isAuthenticating}
              >
                {isAuthenticating ? (
                  <>
                    <Loader2 className="sc-spin" size={16} strokeWidth={2.5} aria-hidden="true" />
                    <span>SIGNING IN...</span>
                  </>
                ) : (
                  <>
                    <span>SIGN IN TO PORTAL</span>
                    <ArrowRight size={16} strokeWidth={2.5} aria-hidden="true" />
                  </>
                )}
              </button>
            </form>

            <div className="sc-footer-links">
              <a
                href="https://smartcosmos.com/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
              <a href="mailto:support@smartcosmos.com">Contact Support</a>
            </div>
          </div>
        </div>
      </div>

      {/* FORGOT PASSWORD MODAL */}
      {isModalVisible && (
        <div className="sc-modal-backdrop" onClick={onModalClose}>
          <div
            className="sc-modal-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sc-forgot-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="sc-modal-x"
              onClick={onModalClose}
              aria-label="Close"
            >
              <X size={18} strokeWidth={2} />
            </button>

            <div className="sc-modal-icon">
              <KeyRound size={20} strokeWidth={2} />
            </div>

            <h3 className="sc-modal-title" id="sc-forgot-title">
              {ModalTitle.FORGOT_PASSWRD}
            </h3>
            <p className="sc-modal-desc">
              Enter your registered email address and we will send a password
              reset link.
            </p>

            {errorAlert && Boolean(forgotPasswordInfo?.error) && (
              <div className="sc-alert sc-alert-error sc-modal-alert">
                <span>{getMutationErrorMessage(forgotPasswordInfo.error)}</span>
              </div>
            )}

            <form onSubmit={onModalSubmit} noValidate>
              <div className="sc-field">
                <label className="sc-label" htmlFor="forgot-email">
                  Email
                </label>
                <input
                  id="forgot-email"
                  type="email"
                  name="email"
                  required
                  placeholder="Enter your registered email"
                  className={`sc-modal-input ${emailError ? "sc-invalid" : ""}`}
                  value={forgotPassword.email}
                  onChange={onEmailChange}
                />
                {emailError && (
                  <span className="sc-field-error">{emailError}</span>
                )}
              </div>

              <div className="sc-modal-actions">
                <button
                  type="button"
                  className="sc-btn-cancel"
                  onClick={onModalClose}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="sc-btn-send"
                  disabled={forgotPasswordInfo.isLoading}
                >
                  {forgotPasswordInfo.isLoading ? "Sending..." : "Send Reset Link"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
